import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, model, signal } from '@angular/core';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { IAttachment } from 'src/app/@core/models/model';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { ToastService } from 'src/app/@core/services/toast.service';
import { FileDropComponent } from 'src/app/@shared/components/file-drop/file-drop.component';
import { MultiFormatPreviewComponent } from 'src/app/@shared/components/multi-format-preview/multi-format-preview.component';
import { GeneralAgentService } from '../../../services/general-agent.service';

export interface TreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  children?: TreeNode[];
}

@Component({
  selector: 'app-manage-skills',
  standalone: true,
  imports: [
    CommonModule,
    DialogComponent,
    ButtonComponent,
    IconComponent,
    MenuComponent,
    FileDropComponent,
    MultiFormatPreviewComponent,
  ],
  templateUrl: './manage-skills.component.html',
  styleUrl: './manage-skills.component.scss',
})
export class ManageSkillsComponent {
  readonly visible = model<boolean>(false);
  private generalAgentService = inject(GeneralAgentService);
  private toastService = inject(ToastService);

  keys = signal<string[]>([]);
  /** Path relative to base prefix, e.g. '' or 'folder1/' or 'folder1/sub/'. Trailing slash for folders. */
  currentPath = signal<string>('');
  loading = signal<boolean>(false);
  addSkillsDialogVisible = signal<boolean>(false);
  uploadFiles = signal<File[]>([]);
  uploading = signal<boolean>(false);
  contextNode = signal<TreeNode | null>(null);
  previewDialogVisible = signal<boolean>(false);
  previewFile = signal<IAttachment | null>(null);
  previewLoading = signal<boolean>(false);

  /** Menu items for a row: files get View + Delete, folders get Delete only. */
  getMenuItemsForNode(node: TreeNode): CustomMenuItem[] {
    const items: CustomMenuItem[] = [];
    if (!node.isFolder) {
      items.push({
        label: 'View',
        icon: 'pi pi-eye',
        callback: () => {
          const n = this.contextNode();
          if (n) this.openView(n);
        },
      });
    }
    items.push({
      label: 'Delete',
      icon: 'pi pi-trash',
      callback: () => {
        const n = this.contextNode();
        if (n) this.deleteNode(n);
      },
    });
    return items;
  }

  /** Base prefix from keys (e.g. 'skills/' or 'bucket/skills/'). */
  basePrefix = computed(() => {
    const k = this.keys();
    return k.length ? this.getSkillsPrefix(k[0]) : '';
  });

  /** Breadcrumb segments: ['skills', 'folder1', 'folder2'] for trail display. */
  breadcrumbSegments = computed(() => {
    const path = this.currentPath();
    const parts = path ? path.split('/').filter(Boolean) : [];
    return ['skills', ...parts];
  });

  /** Direct children (one level) in the current path. Folders first, then files. */
  directChildren = computed(() => this.getDirectChildren(this.keys(), this.basePrefix(), this.currentPath()));

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.loadSkills();
      }
    });
    effect(() => {
      this.keys();
      this.currentPath.set('');
    });
  }

  navigateToFolder(segment: string): void {
    this.currentPath.update((p) => p + segment + '/');
  }

  /** Navigate to path represented by breadcrumb index (0 = root). */
  navigateToBreadcrumb(index: number): void {
    const segments = this.currentPath().split('/').filter(Boolean);
    const newPath = segments.slice(0, index).join('/') + (index > 0 ? '/' : '');
    this.currentPath.set(newPath);
  }

  onDialogVisibleChange(open: boolean): void {
    this.visible.set(open);
  }

  loadSkills(): void {
    this.loading.set(true);
    this.generalAgentService
      .listSkills()
      .then((res) => {
        if (res?.success && res.data?.keys) {
          this.keys.set(res.data.keys);
        } else {
          this.keys.set([]);
        }
      })
      .catch(() => {
        this.keys.set([]);
        this.toastService.error('Failed to load skills');
      })
      .finally(() => this.loading.set(false));
  }

  private getDirectChildren(keys: string[], basePrefix: string, currentPath: string): TreeNode[] {
    if (!keys.length) return [];
    const seen = new Map<string, TreeNode>();
    for (const key of keys) {
      const rel = basePrefix ? key.substring(basePrefix.length) : key;
      if (!rel.startsWith(currentPath)) continue;
      const after = rel.substring(currentPath.length);
      if (!after.length) continue;
      const slash = after.indexOf('/');
      const name = slash === -1 ? after : after.substring(0, slash);
      const isFolder = slash !== -1;
      if (seen.has(name)) {
        const existing = seen.get(name)!;
        if (isFolder) existing.isFolder = true;
      } else {
        const path = basePrefix + currentPath + name + (isFolder ? '/' : '');
        seen.set(name, { name, path, isFolder });
      }
    }
    const arr = Array.from(seen.values());
    return arr.sort((a, b) => (a.isFolder === b.isFolder ? 0 : a.isFolder ? -1 : 1));
  }

  private getSkillsPrefix(firstKey: string): string {
    const idx = firstKey.indexOf('skills/');
    if (idx === -1) return '';
    return firstKey.substring(0, idx + 'skills/'.length);
  }

  openDeleteMenu(event: Event, node: TreeNode, menu: MenuComponent): void {
    this.contextNode.set(node);
    menu.toggle(event);
  }

  openView(node: TreeNode): void {
    this.previewFile.set(null);
    this.previewDialogVisible.set(true);
    this.previewLoading.set(true);
    this.generalAgentService
      .getSkillFileUrl(node.path)
      .then((res) => {
        if (res?.success && res.data?.url) {
          this.previewFile.set({
            filename: node.name,
            url: node.path,
            fullUrl: res.data.url,
          } as IAttachment);
        } else {
          this.toastService.error('Failed to load file');
          this.previewDialogVisible.set(false);
        }
      })
      .catch(() => {
        this.toastService.error('Failed to load file');
        this.previewDialogVisible.set(false);
      })
      .finally(() => this.previewLoading.set(false));
  }

  closePreviewDialog(): void {
    this.previewDialogVisible.set(false);
    this.previewFile.set(null);
  }

  onPreviewDialogVisibleChange(open: boolean): void {
    this.previewDialogVisible.set(open);
    if (!open) {
      this.previewFile.set(null);
    }
  }

  deleteNode(node: TreeNode): void {
    const path = node.path;
    this.generalAgentService
      .deleteSkill(path)
      .then((res) => {
        if (res?.success) {
          this.toastService.success('Deleted successfully');
          this.loadSkills();
        } else {
          this.toastService.error('Failed to delete');
        }
      })
      .catch(() => this.toastService.error('Failed to delete'));
  }

  openAddSkillsDialog(): void {
    this.uploadFiles.set([]);
    this.addSkillsDialogVisible.set(true);
  }

  onUploadData(files: File | File[]): void {
    const list = Array.isArray(files) ? files : [files];
    this.uploadFiles.set(list);
  }

  submitAddSkills(): void {
    const files = this.uploadFiles();
    if (!files.length) {
      this.toastService.error('Please select a ZIP file');
      return;
    }
    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.zip')) {
      this.toastService.error('Please upload a ZIP file');
      return;
    }
    this.uploading.set(true);
    const formData = new FormData();
    formData.append('skillsZip', file, file.name);
    this.generalAgentService
      .uploadSkillsFolder(formData)
      .then((res) => {
        if (res?.success) {
          this.toastService.success('Skills uploaded successfully');
          this.addSkillsDialogVisible.set(false);
          this.uploadFiles.set([]);
          this.loadSkills();
        } else {
          this.toastService.error('Failed to upload skills');
        }
      })
      .catch(() => this.toastService.error('Failed to upload skills'))
      .finally(() => this.uploading.set(false));
  }

  closeAddSkillsDialog(): void {
    this.addSkillsDialogVisible.set(false);
    this.uploadFiles.set([]);
  }
}
